import { useSyncExternalStore } from 'react';
import type { ProjectCardDto, ProjectSnapshotDto, MyDayDto } from '@werft/contracts';
import { api } from './api.js';
import { queryClient } from './queries.js';

/**
 * Aufgaben-Schlüssel (P25, ADR 0028): „KURS-42“ = Projektkürzel + Nummer. Verlinkt werden im Text nur Kürzel, die
 * die Person kennt (aus Lobby, geöffneten Projekten, Mein Tag) – „ISO-9001“ bleibt Text.
 */
export const taskKey = (projectKey: string | undefined, number: number) => (projectKey ? `${projectKey}-${number}` : `#${number}`);

let cached: { at: number; keys: Set<string> } = { at: 0, keys: new Set() };
let version = 0;
const listeners = new Set<() => void>();
queryClient.getQueryCache().subscribe((e) => {
  if (e.type !== 'updated' && e.type !== 'added' && e.type !== 'removed') return;
  const k = e.query.queryKey;
  if ((k[0] === 'ws' && k[2] === 'projects') || (k[0] === 'p' && k[2] === 'snapshot') || (k[0] === 'inbox' && k[1] === 'day')) {
    version++;
    listeners.forEach((l) => l());
  }
});

export function knownKeys(): Set<string> {
  if (cached.at === version && cached.keys.size) return cached.keys;
  const keys = new Set<string>();
  for (const [, d] of queryClient.getQueriesData<{ items: ProjectCardDto[] }>({ queryKey: ['ws'] })) for (const p of d?.items ?? []) if (p && typeof p.key === 'string') keys.add(p.key);
  for (const [, d] of queryClient.getQueriesData<ProjectSnapshotDto>({ queryKey: ['p'] })) if (d && 'project' in d && d.project?.key) keys.add(d.project.key);
  for (const p of queryClient.getQueryData<MyDayDto>(['inbox', 'day'])?.projects ?? []) keys.add(p.key);
  cached = { at: version, keys };
  return keys;
}

/** Bekannte Kürzel als stabiles Set (neu nur, wenn sich Projekte/Snapshots ändern) */
export function useKnownKeys(): Set<string> {
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => version,
  );
  return knownKeys();
}

export const resolveKey = (key: string) => api.get<{ projectId: string; taskId: string }>(`/api/keys/${encodeURIComponent(key)}`);
